import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useProductLaunch } from '@/context/ProductLaunchContext';
import { useTheme } from '@/context/ThemeContext';
import { useBadgeVisibility } from '@/context/BadgeVisibilityContext';

/* ── countdown hook ── */
function calculateCountdown(targetDate: string) {
  const diff = new Date(targetDate).getTime() - Date.now();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, launched: true };
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const minutes = Math.floor((diff % 3600000) / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  return { days, hours, minutes, seconds, launched: false };
}

function useCountdown(targetDate: string) {
  const [time, setTime] = useState(() => calculateCountdown(targetDate));

  useEffect(() => {
    setTime(calculateCountdown(targetDate));
    const id = setInterval(() => setTime(calculateCountdown(targetDate)), 1000);
    return () => clearInterval(id);
  }, [targetDate]);

  return time;
}

function CountdownUnit({ value, label, accent }: { value: number; label: string; accent: string }) {
  return (
    <div className="flex flex-col items-center">
      <div
        className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-xl font-black text-2xl md:text-3xl text-white mb-1"
        style={{ background: `${accent}22`, border: `1.5px solid ${accent}55` }}
      >
        {String(value).padStart(2, '0')}
      </div>
      <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: accent }}>
        {label}
      </span>
    </div>
  );
}

/* ── particle burst on launch ── */
function LaunchedBadge({ accent }: { accent: string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-2 rounded-full font-bold text-sm text-white animate-bounce" style={{ background: accent }}>
      <i className="ri-rocket-line"></i>
      Available Now!
    </div>
  );
}

export default function ProductLaunchSection() {
  const { launch } = useProductLaunch();
  const { theme } = useTheme();
  const { settings } = useBadgeVisibility();
  const light = theme === 'light';
  const countdown = useCountdown(launch.launchDate);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifySent, setNotifySent] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  if (!launch.enabled) return null;

  const accent = launch.accentColor || '#F97316';
  const bg = light ? 'bg-white' : 'bg-[#0D1B2A]';
  const innerBg = light ? 'bg-gray-50 border border-gray-100' : 'bg-[#1C2B3A] border border-white/5';
  const text = light ? 'text-gray-900' : 'text-white';
  const muted = light ? 'text-gray-500' : 'text-white/50';
  const specBg = light ? 'bg-white border border-gray-200' : 'bg-[#0D1B2A] border border-white/10';

  const handleNotify = (e: React.FormEvent) => {
    e.preventDefault();
    if (!notifyEmail) return;
    setNotifySent(true);
    setNotifyEmail('');
  };

  return (
    <section ref={sectionRef} className={`${bg} w-full py-20 transition-colors duration-300 overflow-hidden`}>
      <div className="w-full px-6 md:px-10">

        {/* Section label */}
        <div className="flex items-center gap-3 mb-10">
          {settings.launchBadges && (
            <div className="flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest text-white" style={{ background: accent }}>
              <i className="ri-rocket-line"></i>
              {launch.badge || 'Product Launch'}
            </div>
          )}
          <div className="h-px flex-1 max-w-xs" style={{ background: `linear-gradient(to right, ${accent}60, transparent)` }}></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

          {/* Left — content */}
          <div>
            <h2 className={`text-4xl md:text-5xl font-black leading-tight mb-4 ${text}`}>
              {launch.productName}
            </h2>
            <p className="text-lg font-semibold mb-4" style={{ color: accent }}>
              {launch.tagline}
            </p>
            <p className={`text-sm leading-relaxed mb-8 ${muted}`}>
              {launch.description}
            </p>

            {/* Specs grid */}
            {launch.specs.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
                {launch.specs.map((s, i) => (
                  <div key={i} className={`rounded-lg p-3 text-center ${specBg}`}>
                    <p className="font-black text-lg" style={{ color: accent }}>{s.value}</p>
                    <p className={`text-xs mt-0.5 ${muted}`}>{s.label}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Countdown */}
            {launch.showCountdown && !countdown.launched && (
              <div className="mb-8">
                <p className={`text-xs font-bold uppercase tracking-widest mb-3 ${muted}`}>
                  <i className="ri-time-line mr-1.5"></i>
                  Launching In
                </p>
                <div className="flex items-end gap-3">
                  <CountdownUnit value={countdown.days} label="Days" accent={accent} />
                  <span className={`text-2xl font-black mb-4 ${muted}`}>:</span>
                  <CountdownUnit value={countdown.hours} label="Hours" accent={accent} />
                  <span className={`text-2xl font-black mb-4 ${muted}`}>:</span>
                  <CountdownUnit value={countdown.minutes} label="Min" accent={accent} />
                  <span className={`text-2xl font-black mb-4 ${muted}`}>:</span>
                  <CountdownUnit value={countdown.seconds} label="Sec" accent={accent} />
                </div>
              </div>
            )}

            {countdown.launched && settings.launchBadges && (
              <div className="mb-8">
                <LaunchedBadge accent={accent} />
              </div>
            )}

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-3">
              {countdown.launched ? (
                <Link
                  to={launch.ctaUrl || '/products'}
                  className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-lg font-bold text-sm text-white transition-all hover:opacity-90 whitespace-nowrap"
                  style={{ background: accent }}
                >
                  <i className="ri-compass-3-line"></i>
                  View Launch Details
                </Link>
              ) : (
                <>
                  {/* Notify form */}
                  {launch.showNotifyForm && !notifySent && (
                    <form onSubmit={handleNotify} className="flex gap-2 flex-1">
                      <input
                        type="email"
                        required
                        value={notifyEmail}
                        onChange={(e) => setNotifyEmail(e.target.value)}
                        placeholder="your@email.com"
                        className={`flex-1 px-4 py-3 rounded-lg text-sm border outline-none transition-colors ${
                          light
                            ? 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#F97316]'
                            : 'bg-[#1C2B3A] border-white/10 text-white placeholder-white/30 focus:border-[#F97316]'
                        }`}
                      />
                      <button
                        type="submit"
                        className="flex items-center gap-2 px-5 py-3 rounded-lg font-bold text-sm text-white whitespace-nowrap transition-all hover:opacity-90 cursor-pointer"
                        style={{ background: accent }}
                      >
                        <i className="ri-notification-line"></i>
                        {launch.ctaLabel || 'Notify Me'}
                      </button>
                    </form>
                  )}
                  {notifySent && (
                    <div className="flex items-center gap-2 px-5 py-3 rounded-lg text-sm font-semibold text-emerald-400 bg-emerald-400/10 border border-emerald-400/20">
                      <i className="ri-check-line"></i>
                      You&apos;re on the list! We&apos;ll notify you at launch.
                    </div>
                  )}
                  <Link
                    to={launch.ctaUrl || '/contact'}
                    className={`inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-bold text-sm border transition-all whitespace-nowrap ${
                      light ? 'border-gray-300 text-gray-700 hover:bg-gray-100' : 'border-white/20 text-white hover:bg-white/10'
                    }`}
                  >
                    <i className="ri-information-line"></i>
                    Learn More
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Right — product image */}
          <div className="relative">
            {/* Glow */}
            <div
              className="absolute inset-0 rounded-2xl blur-3xl opacity-20"
              style={{ background: accent }}
            ></div>
            <div className={`relative rounded-2xl overflow-hidden ${innerBg}`}>
              {/* "Coming Soon" ribbon */}
              {!countdown.launched && settings.launchBadges && (
                <div
                  className="absolute top-5 right-5 z-10 px-3 py-1 rounded-full text-xs font-bold text-white"
                  style={{ background: accent }}
                >
                  {launch.badge || 'Coming Soon'}
                </div>
              )}
              <div className="w-full h-72 md:h-96">
                <img
                  src={launch.image}
                  alt={launch.productName}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              {/* Bottom info strip */}
              <div className="p-5 flex items-center justify-between">
                <div>
                  <p className={`font-black text-base ${text}`}>{launch.productName}</p>
                  <p className={`text-xs ${muted}`}>{launch.tagline}</p>
                </div>
                {countdown.launched && settings.launchBadges ? (
                  <span className="text-xs font-bold px-3 py-1 rounded-full text-white" style={{ background: accent }}>
                    Available
                  </span>
                ) : countdown.launched ? null : (
                  <span className={`text-xs font-bold px-3 py-1 rounded-full ${light ? 'bg-gray-100 text-gray-500' : 'bg-white/10 text-white/50'}`}>
                    {countdown.days}d {countdown.hours}h left
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
